import { Alert, Button, MenuItem, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  BenefitActivationDTO,
  BenefitDTOBenefitEnum,
  BenefitTemplateItemDTO,
  CreateBenefitActivationDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SelectControl } from '../../component/form';
import { ActivationDialogExtensions } from '../../module/benefits';
import { SelectedBikeleasingCondition } from '../../module/benefits/bikeleasing/SelectedBikeleasingCondition';
import { useBikeleasingConditions } from '../../module/benefits/bikeleasing/useBikeleasingConditions';
import { BenefitActivationDialogExtensionInterface } from '../interface/benefit-activation-dialog.extension.interface';

interface BikeLeasingBenefitOptions {
  bikeleasingConditionId?: string;
  bikeleasingCompanyCode?: string;
}

const Fields: React.FC = () => {
  const { data: bikeleasingConditionList } = useBikeleasingConditions();

  const { t } = useTranslation('benefitsModule');
  const { control, watch, setValue } = useFormContext();
  const { bikeleasingConditionId }: BikeLeasingBenefitOptions = watch('benefitOptions');
  const selectedCondition = useMemo(
    () =>
      bikeleasingConditionList?.results.find(condition => condition.id === bikeleasingConditionId) || bikeleasingConditionList?.results[0],
    [bikeleasingConditionId, bikeleasingConditionList?.results],
  );

  useEffect(() => {
    setValue('benefitOptions.bikeleasingCompanyCode', selectedCondition?.companyCode);
    setValue('benefitOptions.bikeleasingConditionId', selectedCondition?.id);
  }, [selectedCondition?.companyCode, selectedCondition?.id, setValue]);

  return (
    <>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={1}
        columns={10}
        sx={{
          marginTop: 1,
          mb: 1,
        }}
      >
        <Grid size={10}>
          {!selectedCondition && (
            <Stack sx={{ marginBottom: 2 }}>
              <Alert
                severity="warning"
                action={
                  <Button
                    component={Link}
                    to="/benefits/bikeleasing/integration"
                    sx={{ alignSelf: 'center' }}
                    variant="text"
                    color="inherit"
                    size="small"
                  >
                    {t('BIKELEASING.conditionDetailDialog.alertAction')}
                  </Button>
                }
              >
                {t('BIKELEASING.conditionDetailDialog.alert')}
              </Alert>
            </Stack>
          )}
          <SelectControl
            defaultValue=""
            control={control}
            name="benefitOptions.bikeleasingConditionId"
            label={t('BIKELEASING.fields.condition')}
            fullWidth
            displayEmpty={true}
            margin="dense"
            rules={{ required: true }}
          >
            {!selectedCondition && <MenuItem>{t('BIKELEASING.conditionDetailDialog.noOptions')}</MenuItem>}
            {bikeleasingConditionList?.results.map(condition => (
              <MenuItem key={condition.id} value={condition.id} data-test-id={`option-${condition.id}`}>
                <Typography>{t('BIKELEASING.conditionDetailDialog.title', { companyCode: condition.companyCode })}</Typography>
              </MenuItem>
            ))}
          </SelectControl>
        </Grid>
      </Grid>
      <SelectedBikeleasingCondition bikeleasingCondition={selectedCondition} />
    </>
  );
};

const ChangeOptionsInfo: React.FC = () => {
  const benefit = BenefitDTOBenefitEnum.Bikeleasing.toLowerCase();

  return (
    <Typography
      sx={{
        color: 'text.secondary',
        mb: 2,
      }}
    >
      <Trans
        i18nKey="extensions:bikeleasing.activationDialog.changeOptionsInfo"
        components={{ settings: <Link to={`/benefits/${benefit}/settings`} /> }}
      />
    </Typography>
  );
};

const resetBikeleasingBenefitActivationOptions: ActivationDialogExtensions<BikeLeasingBenefitOptions>['onResetBenefitOptions'] = (
  selectedActivation?: BenefitActivationDTO | BenefitTemplateItemDTO,
): BikeLeasingBenefitOptions => {
  const bikeleasingConditionId = selectedActivation?.bikeleasingOptions?.bikeleasingConditionId;
  const bikeleasingCompanyCode = selectedActivation?.bikeleasingOptions?.bikeleasingCompanyCode;
  return { bikeleasingConditionId, bikeleasingCompanyCode };
};

const createBikeleasingBenefitActivation: ActivationDialogExtensions<BikeLeasingBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  bikeleasingOptions,
  saveActivation,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.Bikeleasing,
    bikeleasingOptions,
  };
  await saveActivation(create);
};

const updateBikeleasingBenefitActivation: ActivationDialogExtensions<BikeLeasingBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  bikeleasingOptions,
  saveActivation,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    bikeleasingOptions,
  };
  await saveActivation(update);
};

export class BikeleasingBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<BikeLeasingBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);
  titleKey? = 'extensions:bikeleasing.activationDialog.createTitle';
  bulkTitleKey? = 'extensions:bikeleasing.activationDialog.bulkCreateTitle';
  ChangeOptionsInfo?: React.ComponentType = ChangeOptionsInfo;
  createActivationHintKey?: string = 'extensions:bikeleasing.activationDialog.activationHint';
  hideSaveButton?: () => boolean = (): boolean => false;

  onCreateActivation = createBikeleasingBenefitActivation;
  onUpdateActivation = updateBikeleasingBenefitActivation;
  onResetBenefitOptions? = resetBikeleasingBenefitActivationOptions;
}
